import { ClipboardEvent, KeyboardEvent, useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { FieldValue, fieldDescribedBy } from "../fields/FieldValue";
import { RichTextEditorProps } from "./RichTextEditor.types";
import { RichTextAttributeDialog } from "./RichTextAttributeDialog";
import { RichTextLinkDialog } from "./RichTextLinkDialog";
import { MergeEntity } from "./RichTextDialogs.types";
import { LinkValues, buildHref, emptyLink, parseHref } from "./linkHref";
import {
  MergeAttribute,
  applyLink,
  applyMergeAttribute,
  linkAround,
  markMergeAttributes,
  readMergeAttribute,
  removeLink,
  selectedText,
} from "./richTextInserts";
import {
  Bold,
  InlineFormat,
  Italic,
  Strikethrough,
  Underline,
  currentRange,
  isFormatActive,
  setBackground,
  setColour,
  setFontFamily,
  setFontSize,
  styleAtSelection,
  toggleFormat,
} from "./richTextCommands";
import {
  Align,
  ListTag,
  blockStateAt,
  flattenBlocks,
  hasBlocks,
  indent,
  insertBlocks,
  outdent,
  setAlign,
  setBlockType,
  splitBlock,
  toggleList,
} from "./richTextBlocks";
import { EditHistory, Snapshot, applySnapshot, takeSnapshot } from "./richTextHistory";
import { RichTextToolbar, DefaultToolbarState, ToggleKey, ToolbarActions, ToolbarState } from "./RichTextToolbar";
import { RichTextFooter } from "./RichTextFooter";
import { toHex } from "./RichTextColourPicker";
import { elementPath } from "./richTextSelection";
import { canonicalFamily, sanitiseEmailHtml } from "./sanitiseEmailHtml";

const FormatsByKey: Record<ToggleKey, InlineFormat> = {
  bold: Bold,
  italic: Italic,
  underline: Underline,
  strike: Strikethrough,
};

const Shortcuts: Record<string, ToggleKey> = { b: "bold", i: "italic", u: "underline" };

const NoEntities: MergeEntity[] = [];

type Dialog =
  | { kind: "link"; range: Range }
  | { kind: "attribute"; range: Range | null; chip: HTMLElement | null };

/**
 * Rich text for an email: words and paragraphs, with the formatting the toolbar offers.
 * Everything entering or leaving is passed through `sanitiseEmailHtml`.
 */
export function RichTextEditor({
  label,
  name,
  value,
  onChange,
  disabled,
  error,
  className,
  helpText,
  mergeEntities = NoEntities,
}: RichTextEditorProps) {
  const editor = useRef<HTMLDivElement>(null);
  const htmlArea = useRef<HTMLTextAreaElement>(null);
  const lastEmitted = useRef<string | null>(null);
  const savedRange = useRef<Range | null>(null);
  const history = useRef(new EditHistory());
  const [active, setActive] = useState<ToolbarState>(DefaultToolbarState);
  const [undoable, setUndoable] = useState({ canUndo: false, canRedo: false });
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [htmlMode, setHtmlMode] = useState(false);
  const [htmlDraft, setHtmlDraft] = useState("");
  const [path, setPath] = useState("");
  const [height, setHeight] = useState<number | null>(null);

  const syncHistory = useCallback(
    () => setUndoable({ canUndo: history.current.canUndo, canRedo: history.current.canRedo }),
    []
  );

  useEffect(() => {
    const clean = sanitiseEmailHtml(value);

    // Only what came from outside is written back, so typing never moves the caret.
    if (editor.current && clean !== lastEmitted.current) {
      editor.current.innerHTML = clean;
      markMergeAttributes(editor.current);
      lastEmitted.current = clean;
      history.current.reset(takeSnapshot(editor.current));
      syncHistory();
    }
  }, [value, syncHistory]);

  // What the toolbar shows, and the footer's path, follow the caret.
  const refresh = useCallback(() => {
    const root = editor.current;
    const range = window.getSelection()?.rangeCount ? window.getSelection()?.getRangeAt(0) : null;

    if (!root || !range || !root.contains(range.commonAncestorContainer)) {
      return;
    }

    savedRange.current = range.cloneRange();
    setActive({
      bold: isFormatActive(root, Bold),
      italic: isFormatActive(root, Italic),
      underline: isFormatActive(root, Underline),
      strike: isFormatActive(root, Strikethrough),
      ...blockStateAt(root),
      size: styleAtSelection(root, "font-size"),
      family: canonicalFamily(styleAtSelection(root, "font-family")),
      colour: toHex(styleAtSelection(root, "color")),
      background: toHex(styleAtSelection(root, "background-color")),
    });
    setPath(elementPath(root, range.startContainer));
  }, []);

  useEffect(() => {
    document.addEventListener("selectionchange", refresh);
    return () => document.removeEventListener("selectionchange", refresh);
  }, [refresh]);

  const emit = useCallback(
    (typing = false) => {
      if (!editor.current) {
        return;
      }

      const clean = sanitiseEmailHtml(editor.current.innerHTML);

      lastEmitted.current = clean;
      history.current.record(takeSnapshot(editor.current), typing);
      syncHistory();
      onChange(clean);
    },
    [onChange, syncHistory]
  );

  const restoreSelection = () => {
    const selection = window.getSelection();
    const inside =
      selection?.rangeCount && editor.current?.contains(selection.getRangeAt(0).commonAncestorContainer);

    if (!inside && savedRange.current) {
      selection?.removeAllRanges();
      selection?.addRange(savedRange.current);
    }
  };

  const restore = (snapshot: Snapshot | null) => {
    const root = editor.current;

    if (!snapshot || !root) {
      return;
    }

    root.focus();
    applySnapshot(root, snapshot);
    markMergeAttributes(root);

    const clean = sanitiseEmailHtml(root.innerHTML);

    lastEmitted.current = clean;
    syncHistory();
    onChange(clean);
  };

  const undo = () => restore(history.current.undo());
  const redo = () => restore(history.current.redo());

  // The browser's own undo (the context menu, say) would not know the editor's edits.
  const latest = useRef({ undo, redo });

  latest.current = { undo, redo };

  useEffect(() => {
    const root = editor.current;
    const onBeforeInput = (event: Event) => {
      const { inputType } = event as InputEvent;

      if (inputType === "historyUndo" || inputType === "historyRedo") {
        event.preventDefault();
        latest.current[inputType === "historyUndo" ? "undo" : "redo"]();
      }
    };

    root?.addEventListener("beforeinput", onBeforeInput);
    return () => root?.removeEventListener("beforeinput", onBeforeInput);
  }, []);

  const insert = (node: Node) => {
    const range = window.getSelection()?.getRangeAt(0);
    const last = node.nodeType === 11 ? node.lastChild : node;

    if (!range || !editor.current?.contains(range.commonAncestorContainer)) {
      return;
    }

    range.deleteContents();
    range.insertNode(node);
    markMergeAttributes(editor.current);

    if (last) {
      range.setStartAfter(last);
      range.collapse(true);
    }

    window.getSelection()?.removeAllRanges();
    window.getSelection()?.addRange(range);
    emit();
  };

  const lineBreak = () => {
    const br = document.createElement("br");

    insert(br);

    // A break at the very end shows nothing until there is something after it.
    if (!br.nextSibling) {
      br.parentNode?.appendChild(document.createElement("br"));
      const range = document.createRange();

      range.setStartAfter(br);
      range.collapse(true);
      window.getSelection()?.removeAllRanges();
      window.getSelection()?.addRange(range);
    }
  };

  // The buttons and lists beside the text act on what was selected there.
  const act = (command: (root: HTMLElement) => boolean) => {
    const root = editor.current;

    if (!root) {
      return;
    }

    root.focus();
    restoreSelection();

    if (command(root)) {
      emit();
    }

    refresh();
  };

  const openLink = () => {
    if (savedRange.current) {
      setDialog({ kind: "link", range: savedRange.current.cloneRange() });
    }
  };

  const openAttribute = (chip: HTMLElement | null = null) => {
    if (chip || savedRange.current) {
      setDialog({ kind: "attribute", range: savedRange.current?.cloneRange() ?? null, chip });
    }
  };

  const toggleHtml = () => {
    if (htmlMode) {
      // What was typed as HTML comes back into the editor, tidied.
      if (editor.current) {
        editor.current.innerHTML = lastEmitted.current ?? "";
        markMergeAttributes(editor.current);
        history.current.record(takeSnapshot(editor.current), false);
        syncHistory();
      }
    } else {
      setHtmlDraft(editor.current ? sanitiseEmailHtml(editor.current.innerHTML) : "");
    }

    setHtmlMode((current) => !current);
  };

  const actions: ToolbarActions = {
    undo,
    redo,
    toggleHtml,
    block: (tag) => act((root) => setBlockType(root, tag)),
    indent: () => act(indent),
    outdent: () => act(outdent),
    align: (align: Align) => act((root) => setAlign(root, align)),
    size: (size) => act((root) => setFontSize(root, size || null)),
    family: (family) => act((root) => setFontFamily(root, family || null)),
    toggle: (key) => act((root) => toggleFormat(root, FormatsByKey[key])),
    colour: (colour) => act((root) => setColour(root, colour)),
    background: (colour) => act((root) => setBackground(root, colour)),
    list: (tag: ListTag) => act((root) => toggleList(root, tag)),
    link: openLink,
    unlink: () =>
      act((root) => {
        const range = currentRange(root);

        return Boolean(range && removeLink(root, range));
      }),
    attribute: () => openAttribute(),
  };

  const finishDialog = () => {
    setDialog(null);
    editor.current?.focus();
  };

  const existingLink = () =>
    editor.current && dialog?.kind === "link" ? linkAround(editor.current, dialog.range) : null;

  const linkInitial = (): LinkValues => {
    const existing = existingLink();

    return existing
      ? {
          ...emptyLink,
          ...parseHref(existing.getAttribute("href") ?? ""),
          text: existing.textContent ?? "",
          newTab: existing.getAttribute("target") === "_blank",
        }
      : { ...emptyLink, text: dialog?.kind === "link" ? selectedText(dialog.range) : "" };
  };

  const onPaste = (event: ClipboardEvent) => {
    event.preventDefault();

    const html = event.clipboardData.getData("text/html");

    if (html) {
      // Pages mark their paragraphs with divs, which are not kept.
      const fragment = document
        .createRange()
        .createContextualFragment(sanitiseEmailHtml(html.replace(/<(\/?)div\b/gi, "<$1p")));

      if (!hasBlocks(fragment)) {
        insert(fragment);
      } else if (editor.current && insertBlocks(editor.current, fragment)) {
        markMergeAttributes(editor.current);
        emit();
      } else {
        insert(flattenBlocks(fragment));
      }

      return;
    }

    const lines = event.clipboardData.getData("text/plain").split(/\r?\n/);
    const fragment = document.createDocumentFragment();

    lines.forEach((line, index) => {
      if (index > 0) {
        fragment.appendChild(document.createElement("br"));
      }

      fragment.appendChild(document.createTextNode(line));
    });
    insert(fragment);
  };

  const onKeyDown = (event: KeyboardEvent) => {
    const key = event.key.toLowerCase();
    const modified = event.ctrlKey || event.metaKey;

    if (event.key === "Enter") {
      event.preventDefault();

      if (event.shiftKey || !editor.current || !splitBlock(editor.current)) {
        lineBreak();
      } else {
        emit();
      }

      return;
    }

    if (modified && key === "z") {
      event.preventDefault();
      (event.shiftKey ? redo : undo)();
      return;
    }

    if (modified && key === "y") {
      event.preventDefault();
      redo();
      return;
    }

    if (modified && Shortcuts[key]) {
      event.preventDefault();
      actions.toggle(Shortcuts[key]);
    }
  };

  return (
    <FieldValue label={label} htmlFor={name} helpText={helpText} error={error} className={className}>
      <div className={classNames("border rounded-2 bg-white", { "border-danger": error })}>
        {!disabled && (
          <RichTextToolbar
            label={label}
            htmlMode={htmlMode}
            state={active}
            canUndo={undoable.canUndo}
            canRedo={undoable.canRedo}
            hasAttributes={mergeEntities.length > 0}
            actions={actions}
          />
        )}
        {htmlMode && (
          <textarea
            ref={htmlArea}
            id={`${name}-html`}
            aria-label={`${label} HTML`}
            className="form-control font-monospace fs-7 border-0 rounded-0 shadow-none"
            style={{ minHeight: "9rem", height: height ?? undefined, resize: "none" }}
            spellCheck={false}
            value={htmlDraft}
            onChange={(event) => {
              const clean = sanitiseEmailHtml(event.target.value);

              setHtmlDraft(event.target.value);
              lastEmitted.current = clean;
              onChange(clean);
            }}
          />
        )}
        <div
          ref={editor}
          id={name}
          role="textbox"
          aria-multiline="true"
          aria-label={label}
          aria-describedby={fieldDescribedBy(name, helpText, error)}
          aria-invalid={error ? true : undefined}
          aria-disabled={disabled ? true : undefined}
          contentEditable={!disabled}
          suppressContentEditableWarning
          className={classNames("form-control border-0 rounded-0 shadow-none", { "d-none": htmlMode })}
          style={{ minHeight: "6rem", height: height ?? undefined, overflowY: "auto" }}
          onInput={() => emit(true)}
          onPaste={onPaste}
          onKeyDown={onKeyDown}
          onDrop={(event) => event.preventDefault()}
          onClick={(event) => {
            const chip = (event.target as Element).closest?.("span.crmattribute") as HTMLElement | null;

            if (chip && !disabled && mergeEntities.length > 0) {
              openAttribute(chip);
            }
          }}
        />
        {!disabled && (
          <RichTextFooter
            path={htmlMode ? "" : path}
            measure={() => (htmlMode ? htmlArea.current : editor.current)?.offsetHeight}
            onResize={setHeight}
          />
        )}
      </div>
      {dialog?.kind === "link" && (
        <RichTextLinkDialog
          initial={linkInitial()}
          entities={mergeEntities}
          onClose={finishDialog}
          onRemove={
            existingLink()
              ? () => {
                  if (editor.current && removeLink(editor.current, dialog.range)) {
                    emit();
                  }

                  finishDialog();
                }
              : undefined
          }
          onApply={(link) => {
            if (editor.current) {
              applyLink(editor.current, dialog.range, {
                href: buildHref(link),
                text: link.text,
                newTab: link.newTab,
              });
              emit();
            }

            finishDialog();
          }}
        />
      )}
      {dialog?.kind === "attribute" && (
        <RichTextAttributeDialog
          entities={mergeEntities}
          initial={
            dialog.chip
              ? readMergeAttribute(dialog.chip)
              : { entity: "1", attribute: "", attributeName: "", defaultValue: "", formatString: "" }
          }
          onClose={finishDialog}
          onApply={(value: MergeAttribute) => {
            const range = dialog.range ?? document.createRange();

            applyMergeAttribute(range, value, dialog.chip);
            emit();
            finishDialog();
          }}
        />
      )}
    </FieldValue>
  );
}
